// This component handles fetching data and business logic for the Menu Preview.

import { DraftCafeStation, DraftMenu, ICafeListItemViewModel, IMenuItemDb } from "@dining/api-client";
import { DayOfWeek } from "@fluentui/react";
import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuPublishedState, enumIterator } from "../../common/commonConstants";
import { fetchCafeAllergenBank, fetchCafeMenusForTheWeek, setNotification } from "../../reducers/diningReducer";
import {
  cafeSelector,
  menuItemBankSelector,
  weekMenuSelector,
  weekStartStringSelector,
} from "../../selectors/diningSelector";
import withAppInsights from "../AppInsights/AppInsights";
import MenuPreviewView from "./MenuPreviewView";
import { PreviewMenu, PreviewMenuItem } from "./types/menuPreviewTypes";

const MenuPreview = () => {
  const dispatch = useDispatch();
  const selectedCafe: ICafeListItemViewModel | undefined = useSelector(cafeSelector);
  const weekStart = useSelector(weekStartStringSelector);
  const menuItemBank = useSelector(menuItemBankSelector);

  // Fetches a week's worth of menus on initalization
  useEffect(() => {
    selectedCafe && dispatch(fetchCafeMenusForTheWeek(selectedCafe.id, weekStart));
    dispatch(setNotification(undefined));
  }, [selectedCafe, weekStart]);

  useEffect(() => {
    selectedCafe && dispatch(fetchCafeAllergenBank(selectedCafe.id));
  }, [selectedCafe]);

  // Converts raw DraftCafeStationsSchedule into a format that we can display
  const weeklyMenuSelector = createSelector(weekMenuSelector, (weeklyMenu) => {
    const menusByDay: Array<PreviewMenu[]> = enumIterator(DayOfWeek).map(() => {
      return [];
    });
    let unpublishedChangesPresent: boolean = false;

    // Don't run unless the weeklyMenu is populated.
    if (weeklyMenu) {
      // For each menu in weeklyMenu
      weeklyMenu?.stations?.forEach((station: DraftCafeStation) => {
        // Sort all the menu items into an object by day of the week.

        // Start with an empty object
        const initialDictionary: { [id: number]: PreviewMenuItem[] } = {};
        // formattedItemsDictionary is the end result.
        const formattedItemsDictionary: { [id: number]: PreviewMenuItem[] } | undefined = station.menu?.reduce(
          // Go through every DraftMenu item in the station's menu...
          (result, item: DraftMenu) => {
            // Find it's entry in the menuItemBank
            const rawData: IMenuItemDb | undefined = menuItemBank.find((entry) => item.menuItemId === entry.id);
            const itemWithData: PreviewMenuItem = {
              ...item,
              itemData: rawData,
            };
            // If any DraftMenu in the weekly menu isn't published, we know that the menu hasn't been published.
            if (!itemWithData.isPublished) unpublishedChangesPresent = true;
            // If we have item DayOfWeek information for the new item (which we always should)
            if (rawData && Array.isArray(item.weekDays)) {
              // For each day of the week our item is scheduled for...
              item.weekDays.forEach((value) => {
                // If there's nothing else in that day's menu slot, make an empty spot for it.
                result[DayOfWeek[value]] = result[DayOfWeek[value]] ?? [];
                // Then, add the previewItem to that day's menu array.
                result[DayOfWeek[value]].push(itemWithData);
              });
            }
            // We return the FULL ARRAY of weekend values to our reduce function, not just the new item.
            return result;
            // initialDictionary is the empty object that every day's menu items get added to.
          },
          initialDictionary,
        );
        // If we have menu items in our formatted dictionary after going through all the menus for a cafe...
        if (formattedItemsDictionary) {
          // Remove all invalid objects
          Object.keys(formattedItemsDictionary)
            .filter((key) => !isNaN(Number(key)))
            .map((v) => Number(v))
            .forEach((dayNumber) => {
              menusByDay[dayNumber].push({
                cafeId: selectedCafe?.id ?? "",
                stationId: station.id ?? "",
                stationName: station.name,
                name: station.name,
                items: formattedItemsDictionary[dayNumber],
              });
            });
        }
      });
    }
    //Dispatch function to change the menu preview message bar state
    if (unpublishedChangesPresent) {
      dispatch(setNotification(MenuPublishedState.UNPUBLISHED_CHANGES));
    }
    // Return our sorted menu, which then is looked at by a selector
    return menusByDay;
  });

  const selectedWeeklyMenu: Array<PreviewMenu[]> = useSelector(weeklyMenuSelector);

  if (selectedCafe && selectedWeeklyMenu) {
    return <MenuPreviewView selectedCafe={selectedCafe} weeklyMenu={selectedWeeklyMenu} />;
  } else {
    return <div />;
  }
};

export default withAppInsights(MenuPreview);
